import { DateTime } from '@ksyos/date-time';
import { useCallback, useEffect, useState } from 'react';

export type BaseParser<T> = {
    /**
     * Parse a string value from URL to the target type
     */
    parse: (value: string | null) => T;
    /**
     * Serialize a value to string for storing in URL
     */
    serialize: (value: T) => string | null;
};

export type ParserBuilder<T> = BaseParser<T | undefined> & {
    /**
     * Add a default value to the parser, making the parsed value non-nullable
     */
    withDefault: (defaultValue: NonNullable<T>) => Parser<NonNullable<T>>;
};

export type Parser<T> = BaseParser<T> & {
    /**
     * Optional default value for the parser
     */
    defaultValue?: T;
};

/**
 * Safe value comparison that handles common edge cases
 */
function safeCompare<T>(a: T, b: T): boolean {
    // Handle identical references and primitive equality
    if (a === b) {
        return true;
    }

    // Handle null/undefined
    if (a == null || b == null) {
        return a == b;
    }

    // Handle Dates
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }

    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((val, idx) => safeCompare(val, b[idx]));
    }

    // For objects, compare their serialized forms
    if (typeof a === 'object' && typeof b === 'object') {
        try {
            return JSON.stringify(a) === JSON.stringify(b);
        } catch {
            // If stringification fails (e.g., circular refs), fall back to reference comparison
            return false;
        }
    }

    return false;
}

/**
 * A hook to manage state in URL query parameters.
 * Inspired by https://github.com/47ng/nuqs
 *
 * @param paramName The parameter name in the URL query string
 * @param parser Parser to convert between URL string and typed value
 * @returns [value, setValue] tuple similar to useState
 */
export function useQueryState<T>(
    paramName: string,
    parser: Parser<NonNullable<T>>
): [NonNullable<T>, (value: NonNullable<T> | ((prev: NonNullable<T>) => NonNullable<T>)) => void];

export function useQueryState<T>(paramName: string, parser: Parser<T>): [T, (value: T | ((prev: T) => T)) => void];

export function useQueryState<T>(paramName: string, parser: Parser<T>) {
    const serialize = parser.serialize;
    const parse = parser.parse;
    const defaultValue = parser.defaultValue as T;

    const getValueFromUrl = useCallback(() => {
        if (typeof window === 'undefined') {
            return defaultValue;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const paramValue = searchParams.get(paramName);
        const parsedValue = parse(paramValue);

        // Clean up URL if needed (invalid value or equals default)
        if (paramValue !== null) {
            const isDefault = safeCompare(parsedValue, defaultValue);
            const isValidParse = safeCompare(parsedValue, parse(paramValue));

            if (isDefault || !isValidParse) {
                if (isDefault) {
                    searchParams.delete(paramName);
                } else {
                    const serialized = serialize(parsedValue);
                    if (!serialized) {
                        searchParams.delete(paramName);
                    } else {
                        searchParams.set(paramName, serialized);
                    }
                }
                const newSearch = searchParams.toString();
                const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
                window.history.replaceState(null, '', newUrl);
            }
        }

        return parsedValue;
    }, [paramName, parse, defaultValue, serialize]);

    const [value, setValue] = useState<T>(getValueFromUrl());

    const updateValue = useCallback(
        (newValue: T | ((prev: T) => T)) => {
            const resolvedValue = typeof newValue === 'function' ? (newValue as (prev: T) => T)(value) : newValue;

            setValue(resolvedValue);

            if (typeof window === 'undefined') {
                return;
            }

            const searchParams = new URLSearchParams(window.location.search);
            const isDefault = safeCompare(resolvedValue, defaultValue);

            if (isDefault) {
                searchParams.delete(paramName);
            } else {
                const serialized = serialize(resolvedValue);
                if (!serialized) {
                    searchParams.delete(paramName);
                } else {
                    searchParams.set(paramName, serialized);
                }
            }

            const newSearch = searchParams.toString();
            const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
            window.history.replaceState(null, '', newUrl);
        },
        [paramName, serialize, value, defaultValue]
    );

    useEffect(() => {
        const handleUrlChange = () => {
            setValue(getValueFromUrl());
        };

        window.addEventListener('popstate', handleUrlChange);

        return () => window.removeEventListener('popstate', handleUrlChange);
    }, [getValueFromUrl]);

    return [value, updateValue] as const;
}

/**
 * Helper function to create a parser with consistent behavior
 */
export function createParser<T>(config: {
    parse: (value: string | null) => T | undefined;
    serialize: (value: T | undefined) => string | null;
}): ParserBuilder<T> {
    const parser: ParserBuilder<T> = {
        parse: config.parse,
        serialize: config.serialize,

        withDefault: (defaultValue: NonNullable<T>): Parser<NonNullable<T>> => ({
            defaultValue,

            parse: (value: string | null): NonNullable<T> => {
                const parsed = config.parse(value);

                return (parsed as NonNullable<T>) ?? defaultValue;
            },
            serialize: (value: NonNullable<T>) => config.serialize(value),
        }),
    };

    return parser;
}

/**
 * Parser for string values
 */
export const parseAsString = createParser<string>({
    parse: (value: string | null) => value ?? undefined,
    serialize: (value: string | undefined) => value ?? null,
});

/**
 * Parser for boolean values
 */
export const parseAsBoolean = createParser<boolean>({
    parse: (value: string | null) => {
        if (value === null) {
            return undefined;
        }

        if (value === 'true') {
            return true;
        }
        if (value === 'false') {
            return false;
        }

        return undefined;
    },
    serialize: (value: boolean | undefined) => (typeof value === 'boolean' ? String(value) : null),
});

/**
 * Parser for Date objects in DD-MM-YYYY format
 */
export const parseAsDate = createParser<Date>({
    parse: (value: string | null) => (value ? DateTime.parse(value, 'DD-MM-YYYY').toDate() : undefined),
    serialize: (value: Date | undefined) => (value ? DateTime.from(value).format('DD-MM-YYYY') : null),
});

/**
 * Creates a parser for arrays from an item parser
 */
export function parseAsArrayOf<T>(itemParser: Parser<T | undefined>): ParserBuilder<T[] | undefined> {
    return createParser<T[]>({
        parse: (value: string | null) => {
            if (!value) {
                return undefined;
            }
            const parsed = value
                .split(',')
                .map((item) => itemParser.parse(item))
                .filter((item): item is T => item !== undefined);

            return parsed.length > 0 ? parsed : undefined;
        },
        serialize: (value: T[] | undefined) => {
            if (!value || value.length === 0) {
                return null;
            }
            const serialized = value
                .map((item) => itemParser.serialize(item))
                .filter((item): item is string => item !== null);

            return serialized.length > 0 ? serialized.join(',') : null;
        },
    });
}
